import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Chip from 'tio-ui/components/chip';
import PageHeader from 'tio-ui/components/page-header';
import ElfTile from 'tio-employee/components/slr/elf-tile';
import ReimaginedContributionHistory from 'tio-employee/components/slr/contribution-history';
import LoanStandingAttentionCard from 'tio-employee/components/slr/loan-standing-attention-card';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import {
  elfTileState,
  isLrapEnrolled,
  shouldShowElfTile,
} from 'tio-employee/utils/lrap-enrollment';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type FeaturesService from 'tio-common/services/features';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;

  // Post-upload refresh result, keyed to the model value it superseded. A
  // route refresh (e.g. switching the receiving account re-runs the model
  // hook) replaces the model value, which invalidates this override
  // automatically — a plain tracked copy seeded from args would keep showing
  // the OLD account's alert after the model refreshed underneath it.
  @tracked private refreshedLoanStanding?: {
    supersedes: SlrLoanStandingAuditState | null;
    state: SlrLoanStandingAuditState | null;
  };

  get loanStandingVerification() {
    const fromModel = this.args.model.loanStandingVerification;
    const refreshed = this.refreshedLoanStanding;
    return refreshed && refreshed.supersedes === fromModel ? refreshed.state : fromModel;
  }

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  /**
   * The card's verdict is scoped to `accountReceivingContributionsId`, so this
   * is the account it is talking about — passed in only so the cross-account
   * mismatch strip can name it (#10263). Read off the model's person, which
   * the route includes accounts for, rather than sessionContext, so it tracks
   * a route refresh after the receiving account changes.
   */
  get loanStandingAccountName() {
    return this.args.model.person?.accountReceivingContributions?.institutionName ?? null;
  }

  get lrapEnrollmentStatus() {
    return this.args.model.lrapEnrollmentStatus;
  }

  get showElfTile() {
    return shouldShowElfTile(this.features.isEnabled('lrapEnrollment'), this.lrapEnrollmentStatus);
  }

  get elfTileState() {
    return elfTileState(this.lrapEnrollmentStatus);
  }

  get isLrapEnrolled() {
    return isLrapEnrolled(this.lrapEnrollmentStatus);
  }

  /**
   * The plan's selected eligibility-condition names — drives which upload
   * guidelines the verification card lists (tio-admin →
   * plans/eligibility-conditions-editor is the source of truth).
   */
  get planEligibilityConditions() {
    return this.sessionContext.currentEmployee?.plan?.eligibilityConditions?.map((c) => c.name);
  }

  /**
   * A live upload from THIS session that hasn't been dismissed yet — its
   * file-anchored feedback lives on the attention card, so the card must
   * outlive verdicts that would otherwise swap the page's presentation.
   */
  get hasLiveUploadActivity() {
    return this.slrLoanStandingVerification.liveUpload.phase !== 'idle';
  }

  get showLoanStandingAlert() {
    // Hard-ineligible verdicts render the page-level banner instead of an
    // attention card (Q1, page states 670-466 / 1031-2202) — the banner lives
    // in contribution-history, which owns the layout slot between hero and
    // Loan Accounts. EXCEPT while this session's upload feedback is still
    // up: hiding the card would erase the verdict strip the user is reading
    // (it clears on dismiss).
    if (this.loanStandingVerification?.status === 'ineligible') {
      return this.hasLiveUploadActivity;
    }
    return shouldShowSlrLoanStandingAlert(this.loanStandingVerification);
  }

  /**
   * The state flipped to eligible during THIS session (post-upload refresh) —
   * show the success confirmation once. A page that loads already-eligible
   * stays quiet (no attention section), matching the design's verified state.
   */
  get justVerified() {
    const refreshed = this.refreshedLoanStanding;
    if (refreshed?.state?.status !== 'eligible') return false;
    // Same identity check as loanStandingVerification: the refresh only
    // counts while it supersedes the CURRENT route model. After an account
    // switch/route refresh replaces the model, a stale eligible refresh must
    // not resurrect the success section over a page that loaded verified.
    if (refreshed.supersedes !== this.args.model.loanStandingVerification) return false;
    const previous = refreshed.supersedes;
    return !!previous && previous.status !== 'eligible';
  }

  get showAttentionSection() {
    return this.showLoanStandingAlert || this.justVerified;
  }

  /**
   * Records the Review Name panel answer (fuzzy name match). The service
   * PATCHes name_match_confirmation and returns the settled post-eval state,
   * applied through the same supersedes-tracking as the upload refresh so
   * justVerified fires when a confirmation flips the state to eligible.
   */
  confirmLoanStandingName = async (confirmed: boolean) => {
    const id = this.loanStandingVerification?.employeePlanAccountId;
    if (!id) return;
    const supersedes = this.args.model.loanStandingVerification;
    const state = await this.slrLoanStandingVerification.confirmNameMatch(id, confirmed);
    this.refreshedLoanStanding = { supersedes, state };
  };

  refreshLoanStandingVerification = dropTask(async () => {
    const supersedes = this.args.model.loanStandingVerification;
    // Post-write reader: the upload/evaluation never invalidates this query's
    // soft-cached document, so without reload a settled verdict can re-render
    // the pre-upload state. The verdict fetch comes FIRST — the route refresh
    // below re-runs a model hook whose Promise.all mostly fails closed, and a
    // rejection there must not leave the card stuck pre-verdict.
    const state = await this.slrLoanStandingVerification.fetchCurrentState({ reload: true });
    this.refreshedLoanStanding = { supersedes, state };
    // A settled verdict usually follows a reflection that added or updated
    // loan accounts — reload the route model so the Loan Accounts section
    // shows them without a manual page refresh (the model's person fetch is
    // already reload: true). Best-effort: the verdict is already applied
    // above, and a fresh model carries it anyway (which is also why the
    // supersedes identity check invalidating the override is fine).
    try {
      await this.router.refresh('authenticated.slr.dashboard');
    } catch (e) {
      console.warn('[SLR loan standing] Route refresh after verdict failed', e);
    }
  });

  <template>
    <div class="tio-col-tight">
      <PageHeader @title={{t "dashboard_tiles.student_loan_repayment"}} @sticky={{false}}>
        <:actions>
          {{#if this.isLrapEnrolled}}
            <Chip @intent="success" @size="sm" data-test-lrap-enrolled-chip>
              {{t "lrap.enrollment.tile.enrolled"}}
            </Chip>
          {{/if}}
        </:actions>
      </PageHeader>
      {{#if this.showElfTile}}
        <ElfTile @state={{this.elfTileState}} />
      {{/if}}
      <ReimaginedContributionHistory
        @model={{this.reimaginedModel}}
        @loanStandingState={{this.loanStandingVerification}}
        @slotAttentionCount={{if this.showAttentionSection 1 0}}
      >
        <:attention>
          {{! Card only — the unified "Needs Your Attention" section (one
              header covering this card and the server action items) is
              contribution-history's; it places the section below the hero,
              or at the top for page states without one (#9887, #9910). }}
          {{#if this.loanStandingVerification}}
            {{#if this.showAttentionSection}}
              <LoanStandingAttentionCard
                @state={{this.loanStandingVerification}}
                @accountInstitutionName={{this.loanStandingAccountName}}
                @eligibilityConditions={{this.planEligibilityConditions}}
                @onRefresh={{this.refreshLoanStandingVerification.perform}}
                @onConfirmName={{this.confirmLoanStandingName}}
                data-test-slr-loan-standing-attention
              />
            {{/if}}
          {{/if}}
        </:attention>
      </ReimaginedContributionHistory>
    </div>
  </template>
}
